/**
 * Per `fan-youtube-integration` — shared types for the YouTube
 * member-list + tier caches. These mirror the on-DDB layout of
 * `nx-yt-channel-members-<env>` and `nx-yt-channel-tiers-<env>` so
 * handler + UI code share one source of truth.
 *
 * The cache is hard-state, owned by the scheduled members poller.
 * Request-path handlers READ ONLY; they MUST NOT call YouTube live.
 */
import { z } from 'zod';
/**
 * One cached fan-membership row. Key = (channelId, memberChannelId).
 * `expiredAt` is set (not deleted) when the fan stops being a member
 * for 2 consecutive poll cycles — preserves audit / "was a member
 * from X to Y" history.
 */
export const YoutubeMembershipCacheRowSchema = z.object({
    /** Bound character's YouTube channel — the host channel. */
    channelId: z.string().regex(/^UC[A-Za-z0-9_-]{22}$/),
    /** The fan's YouTube channel — they are a member OF channelId. */
    memberChannelId: z.string().regex(/^UC[A-Za-z0-9_-]{22}$/),
    /** Channel's membership tier the fan currently holds. */
    level: z.string().min(1).max(80),
    /** ISO timestamp — when YouTube reports membership began. */
    since: z.string().datetime(),
    /** ISO timestamp — last successful poll that saw this row. */
    lastSeenAt: z.string().datetime(),
    /**
     * ISO timestamp — set when the row first missed 2 consecutive polls.
     * Once set, `getCachedMembership` treats the fan as NOT a current
     * member, but the row remains as audit trail.
     */
    expiredAt: z.string().datetime().optional(),
});
/**
 * One cached tier-level row. Key = (channelId, levelId). Refreshed
 * once per poll cycle from `youtube.membershipsLevels.list`. The
 * `displayName` is what we surface on the member badge.
 */
export const YoutubeChannelTierRowSchema = z.object({
    channelId: z.string().regex(/^UC[A-Za-z0-9_-]{22}$/),
    /** YouTube's opaque tier id; matches the `level` field on member rows. */
    levelId: z.string().min(1).max(80),
    /** Tier name the channel owner configured (e.g. "贊助會員"). */
    displayName: z.string().min(1).max(120),
    /**
     * Monthly price in the YouTube-reported currency (typically the
     * channel's home currency). Optional — not all tiers expose price.
     */
    priceMicros: z.number().int().min(0).optional(),
    priceCurrency: z.string().length(3).optional(),
    /** ISO timestamp — last poll that refreshed this tier row. */
    refreshedAt: z.string().datetime(),
});
/**
 * Response shape for `GET /api/fan/characters/{id}/youtube-membership`.
 * `source: 'cache'` is the only valid value — the endpoint NEVER
 * falls through to live YouTube. `cacheAgeSec: null` signals the
 * poller hasn't run for this channel yet (UI should show "資料載入中"
 * not "不是會員").
 */
export const FanCharacterMembershipResponseSchema = z.union([
    z.object({
        isMember: z.literal(true),
        level: z.string(),
        levelDisplayName: z.string(),
        since: z.string().datetime(),
        source: z.literal('cache'),
        cacheAgeSec: z.number().int().min(0),
    }),
    z.object({
        isMember: z.literal(false),
        reason: z.enum([
            'no-linked-youtube',
            'character-has-no-channel',
            'feature-disabled',
            'cache-pending',
            'not-a-member',
        ]),
        source: z.literal('cache'),
        cacheAgeSec: z.number().int().min(0).nullable(),
    }),
]);
